import React, { useEffect, useRef, useState, useContext } from "react";
import Chart from "chart.js/auto";
import { ApiContext } from "../../../../../context/MyContext";
import * as userService from "../../../../../service/userService";
import { parseLocalDate } from "../../../../../utils/formatLocalDate";
import { formatLocalDate } from "../../../../../utils/formatLocalDate";

const SleepGraphModal = ({ profileId, selectedDate, eventTypeId, onClose }) => {
  const chartRef = useRef(null);
  const { get } = useContext(ApiContext);

  const [range, setRange] = useState("D");
  const [sleepData, setSleepData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [averageSleep, setAverageSleep] = useState(0); // in minutes
  const [recommended, setRecommended] = useState(8); // default 8 hrs
  const [dailyStreak, setDailyStreak] = useState(0);
  const [hasData, setHasData] = useState(true);
  const [percentages, setPercentages] = useState([]);

  const fetchChartData = async () => {
    const result = await userService.fetchSleepSummary(
      get,
      profileId,
      range,
      selectedDate,
      eventTypeId
    );

    if (result.success) {
      const data = result.data || [];

      setRecommended(data[0]?.recommended || 8);

      if (range === "D") {
        setAverageSleep(result.average_duration_minutes || 0);
      } else {
        setAverageSleep((result.average_duration_hours || 0) * 60);
      }

      setDailyStreak(result.daily_streak || 0);

      if (data.length === 0) {
        setSleepData([]);
        setLabels([]);
        setHasData(false);
        return;
      }

      setHasData(true);
      setPercentages(data.map((d) => d.percentage ?? null));
      const sortedDates = data
        .map((d) => parseLocalDate(d.date))
        .sort((a, b) => a - b);
      const startDate = new Date(sortedDates[0]);
      startDate.setHours(0, 0, 0, 0);

      const numDays = range === "D" ? 1 : 7;

      const filledData = Array.from({ length: numDays }, (_, i) => {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        const dateStr = formatLocalDate(date);
        const entry = data.find((d) => d.date === dateStr);
        return {
          label:
            range === "D"
              ? date.toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })
              : date.toLocaleDateString("en-US", { weekday: "short" }),
          value: entry ? entry.value : 0,
        };
      });

      setLabels(filledData.map((d) => d.label));
      setSleepData(filledData.map((d) => d.value));
    } else {
      setHasData(false);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, [range, selectedDate]);

  useEffect(() => {
    if (!chartRef.current || !sleepData.length) return;

    const chart = new Chart(chartRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Sleep Duration",
            data: sleepData,
            borderColor: "#FF6414",
            backgroundColor: "#FF6414",
            borderWidth: 2,
            tension: 0.3,
            pointRadius: 3,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: (val) => `${val} hrs`,
            },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const index = ctx.dataIndex;
                const sleepHours = ctx.raw;
                const percent =
                  percentages[index] != null ? ` (${percentages[index]}%)` : "";
                const recommendedText = recommended
                  ? `Recommended: ${recommended} hrs`
                  : "";
                return [`Sleep: ${sleepHours} hrs${percent}`, recommendedText];
              },
            },
          },
        },
      },
      plugins: [
        {
          id: "recommendedLine",
          afterDraw(chart) {
            const {
              ctx,
              chartArea: { left, right },
              scales,
            } = chart;
            const y = scales.y.getPixelForValue(recommended);
            ctx.save();
            ctx.setLineDash([4, 4]);
            ctx.strokeStyle = "#FF6414";
            ctx.beginPath();
            ctx.moveTo(left, y);
            ctx.lineTo(right, y);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = "#FF6414";
            ctx.font = "12px sans-serif";
            ctx.fillText(`Recommended: ${recommended} hrs`, left + 5, y - 5);
            ctx.restore();
          },
        },
      ],
    });

    return () => chart.destroy();
  }, [sleepData, labels, recommended]);

  return (
    <div className="fixed inset-0 bg-orange-primary bg-opacity-20 z-50 flex justify-center items-center">
      <div className="bg-white w-[90%] max-w-3xl rounded-lg p-4 relative">
        <button
          onClick={onClose}
          className="absolute top-2 right-4 text-gray-700"
        >
          ✕
        </button>
        <h2 className="text-xl font-bold mb-2 text-center">Sleep Analytics</h2>

        <div className="flex justify-center gap-4 mb-4">
          {["D", "W"].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1 rounded-full border ${
                r === range
                  ? "bg-orange-primary text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              {r}
            </button>
          ))}
        </div>

        {hasData && sleepData.length > 0 ? (
          <div className="w-full h-[300px]">
            <canvas ref={chartRef}></canvas>
          </div>
        ) : (
          <div className="w-full h-[300px] flex items-center justify-center text-gray-500 font-medium">
            No sleep data available for this range
          </div>
        )}

        <div className="mt-4 text-center font-medium space-y-1">
          <div>
            <span className="text-gray-700">Average Sleep: </span>
            <span className="text-black">
              {(averageSleep / 60).toFixed(1)} hrs
            </span>
          </div>
          <div>
            <span className="text-gray-700">Daily Streak: </span>
            <span className="text-black">{dailyStreak} days</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SleepGraphModal;
